export type CarouselLogo = {
  name: string;
  src: string;
};

const logos: readonly CarouselLogo[] = [
  { name: 'Kancelarija za IT', src: '/images/kancelarija-za-it.png' },
  { name: 'Ministarstvo pravde Republike Srbije', src: '/images/pravda.png' },
  { name: 'Cropt', src: '/images/cropt.png' },
  { name: 'Inspira grupa', src: '/images/inspira.png' }
];

// Keep styles local so this component is independent of TechnologyCarousel.
const carouselStyles = `
  .logo-carousel__viewport {
    overflow: hidden;
    mask-image: linear-gradient(to right, transparent, black 6%, black 94%, transparent);
  }

  .logo-carousel__track {
    display: flex;
    width: max-content;
    animation: datasliv-logo-scroll linear infinite;
  }

  .logo-carousel__group {
    display: flex;
    flex-shrink: 0;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .logo-carousel__item {
    display: flex;
    flex: 0 0 224px;
    align-items: center;
    justify-content: center;
    width: 224px;
    height: 128px;
    padding: 8px 24px;
  }

  .logo-carousel__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0.9;
    filter: grayscale(100%) brightness(0.65) contrast(1.25);
    transition: filter 400ms ease, opacity 400ms ease;
  }

  .dark .logo-carousel__image {
    filter: grayscale(100%) brightness(0.85) contrast(1.1);
    opacity: 1;
  }

  .logo-carousel__item:hover .logo-carousel__image {
    /* A fine shadow keeps the original white lettering legible in light mode. */
    filter: grayscale(0%) brightness(1) contrast(1) drop-shadow(0 0 1px rgba(7, 17, 29, 0.5));
    opacity: 1;
  }

  .dark .logo-carousel__item:hover .logo-carousel__image {
    filter: grayscale(0%) brightness(1) contrast(1);
  }

  @keyframes datasliv-logo-scroll {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }

  @media (prefers-reduced-motion: reduce) {
    .logo-carousel__viewport { mask-image: none; }
    .logo-carousel__track { width: 100%; animation: none; }
    .logo-carousel__group { width: 100%; flex-wrap: wrap; justify-content: center; }
    .logo-carousel__item { flex-basis: min(224px, 50%); }
    .logo-carousel__group[aria-hidden],
    .logo-carousel__item[aria-hidden] { display: none; }
    .logo-carousel__image { transition: none; }
  }
`;

type LogoCarouselProps = {
  logos?: readonly CarouselLogo[];
};

export const LogoCarousel = ({ logos: items = logos }: LogoCarouselProps) => {
  if (items.length === 0) {
    return null;
  }

  // Each half must cover the max-w-7xl viewport, even with a short logo list.
  const repetitions = Math.ceil(1280 / (items.length * 224));
  const sequence = Array.from({ length: repetitions }, () => items).flat();
  // Fixed-width slots and a proportional duration keep the speed at 36px/s.
  const duration = `${(sequence.length * 224) / 36}s`;

  return (
    <section aria-label="Logos" className="overflow-hidden py-8 sm:py-10">
      <style>{carouselStyles}</style>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="logo-carousel__viewport">
          <div
            className="logo-carousel__track"
            style={{ animationDuration: duration }}
          >
            {[0, 1].map(group => (
              <ul
                key={group}
                className="logo-carousel__group"
                aria-hidden={group === 1 ? true : undefined}
              >
                {sequence.map((logo, index) => (
                  <li
                    key={`${logo.src}-${index}`}
                    className="logo-carousel__item"
                    aria-hidden={index >= items.length ? true : undefined}
                  >
                    <img
                      className="logo-carousel__image"
                      src={logo.src}
                      alt={logo.name}
                      width={176}
                      height={112}
                      decoding="async"
                      draggable={false}
                    />
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
